<template>
  <section class="h-full mx-4">
    <BaseTable
      v-if="vatFactors"
      :headers="vatFactorsHeaders"
      :list="vatFactors"
      clickable
      @add-new="onAddNewVatFactor"
      @table-item-clicked="onEditVatFactor"
      @deleteMultiselect="onDeleteMultiselect"
      @refresh-data="loadData"
      table-id="vat-factors"
      @apply-filters="onApplyFilters"
      @reset-filters="onResetFilters">
      <template #actions="{ item }: any">
        <div class="flex items-center justify-center gap-1">
          <ActionButton
            :isEnabled="item.isFromManager"
            color="red"
            :icon="IconTrashOutline"
            @action="onDeleteRow(item as VatFactor)" />
        </div>
      </template>
      <template #filters>
        <section class="flex flex-col items-start justify-start w-full max-w-xl py-4 mx-auto">
          <div>
            <label class="space-y-2 text-sm tracking-wide text-gray-400 uppercase">{{
              $t('baseObject.isActive')
            }}</label>
            <BaseTriStateToggle
              :label="$t('baseObject.isActive')"
              name="isActive"
              v-model="filterIsActive" />
          </div>
        </section>
      </template>
    </BaseTable>

    <VatFactorsModalCreateEdit
      v-if="createEditModalActive"
      :mode="createEditModalMode"
      :vat-factor="vatFactor"
      @close-modal="createEditModalActive = false"
      @save="onSave">
    </VatFactorsModalCreateEdit>
    <BaseModalConfirm
      v-if="modalDeleteRow"
      @cancel="modalDeleteRow = false"
      @confirm="onDeleteSingle()">
      <template #title>{{ $t('common.actions.delete') }}</template>
      <template #text>
        {{ $t('messages.info.deleteSingle', { 'item': deleteRowItem?.name }) }}
      </template>
      <template #buttonText>{{ $t('common.actions.delete') }}</template>
    </BaseModalConfirm>
  </section>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import { useToastStore } from '@/stores/toast';
import { useI18n } from 'vue-i18n';

import { getVatFactors, removeVatFactorsByIds } from '@/services/api/vat';
import { filterActive, filterData, sortData } from '@/utils/table';
import VatFactorsModalCreateEdit from '@/components/prices/VatFactorsModalCreateEdit.vue';
import BaseTable from '@/components/common/BaseTable.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';
import ActionButton from '@/components/common/ActionButton.vue';
import BaseTriStateToggle from '@/components/common/BaseTriStateToggle.vue';

import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';

import type { TableFilterData } from '@/types/Base';
import type { VatFactor } from '@/types/Vat';

const { t: $t } = useI18n({ useScope: 'global' });

const toastStore = useToastStore();

const vatFactors = ref<VatFactor[]>([]);
const filterIsActive = ref<boolean | undefined>();
const vatFactorsHeaders = [
  { value: 'code', text: $t('baseObject.code'), type: 'text', active: true },
  { value: 'name', text: $t('baseObject.name'), type: 'text', active: true },
  { value: 'vatCategory.name', text: $t('salesDocuments.vatCategory'), type: 'text', active: true },
  { value: 'vatLevel.name', text: $t('vatLevel'), type: 'text', active: true },
  { value: 'vatFactorValue', text: $t('mw-params.value'), type: 'text', active: true },

  { value: 'boid', text: $t('baseObject.boid'), type: 'text', active: true },
  { value: 'isActive', text: $t('baseObject.isActive'), type: 'check', active: true },
  { value: 'actions', text: $t('baseObject.actions'), active: true },
];

onMounted(async () => {
  await loadData();
});

const vatFactor = ref<VatFactor>(initializeVatFactor());

const createEditModalActive = ref(false);

const createEditModalMode = ref<'add' | 'edit'>('add');

function initializeVatFactor(): VatFactor {
  return {
    id: uuidv4(),
    name: '',
    code: '',
    vatFactorValue: 0,
    vatCategoryId: '',
    vatLevelId: '',
    isActive: true,
    isFromManager: true,
  };
}

function onAddNewVatFactor() {
  vatFactor.value = initializeVatFactor();
  createEditModalActive.value = true;
  createEditModalMode.value = 'add';
}

function onEditVatFactor(item: VatFactor) {
  vatFactor.value = item;
  createEditModalActive.value = true;
  createEditModalMode.value = 'edit';
}

function onSave(modifiedData: VatFactor) {
  if (!vatFactors.value) return;

  const index = vatFactors.value.findIndex((item) => item.id === modifiedData.id);
  if (index !== -1) vatFactors.value[index] = modifiedData;
  else vatFactors.value.push(modifiedData);
  createEditModalActive.value = false;
}

async function loadData(data?: TableFilterData) {
  let response = await getVatFactors();
  if (!response) return;

  if (data && data.query) {
    response = filterData(response, data.query, ['name', 'code']);
  }

  if (data && data.column !== undefined && data.order) {
    response = sortData(response, data.column, data.order, vatFactorsHeaders);
  }

  if (data && data.isActive !== undefined) response = filterActive(response, data.isActive);

  vatFactors.value = response;
}

async function onDeleteMultiselect(items: VatFactor[]) {
  const idsToDelete = items.filter((item) => item.isFromManager === true).map((item) => item.id);
  const res = await removeVatFactorsByIds(idsToDelete);
  if (!res && vatFactors.value)
    vatFactors.value = vatFactors.value.filter((type) => !idsToDelete.includes(type.id));
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });
}

async function onDeleteSingle() {
  modalDeleteRow.value = false;
  if (!deleteRowItem.value) return;
  const idToDelete = deleteRowItem.value.id;
  const res = await removeVatFactorsByIds([idToDelete]);
  if (!res && vatFactors.value)
    vatFactors.value = vatFactors.value.filter((type) => type.id !== idToDelete);
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });
}

const modalDeleteRow = ref(false);

const deleteRowItem = ref<VatFactor>();
function onDeleteRow(item: VatFactor) {
  deleteRowItem.value = item;
  modalDeleteRow.value = true;
}

function onApplyFilters(data: TableFilterData) {
  data.isActive = filterIsActive.value ?? undefined;
  loadData(data);
}

function onResetFilters() {
  filterIsActive.value = undefined;
  loadData({});
}
</script>
